import { useEffect, useRef, useCallback, useMemo, useState } from 'react';
import {
  useAppSelector,
  getExtraDetails,
  getSearchKey,
  getWorkspaceId,
  dispatch,
  updateProductLineItems,
  ErrorVariants,
  showToast,
  ProductVariant,
  CategoryTreeItem,
  useGetProductsByCustomerMutation,
  updateSearchKey,
  addItemToCart,
  removeItemFromCart,
  updateItemInCart,
  OfflineTaskType,
  getCurrentCustomer,
  getCartByCustomer,
  getCustomerInfo,
  OfflineCurrentCustomerMetadata,
  getCheckInInfo,
  ActiveOrderLine,
  UOM,
  APP_TYPE,
  getAppType,
  getClockInInfo,
  getFieldAutomationConfig,
  getCatalogConfig,
  ProductListResponse,
  removeSearchKey,
} from '@zonofi/zono-money-store';
import { OrderProductActionType, SortDirection } from '../../constants';
import {
  FilterConfig,
  useDebounceSearch,
  useLoadAllApiDataV2,
} from '../../hooks';
import { isEmpty, isNil } from 'lodash';
import { useParams } from 'react-router-dom';
import {
  ChipDataItem,
  ChipFilterSection,
  PriceUpdatedProduct,
  ProductPriceUpdateMap,
  TripInfoTaskType,
} from '../../types';
import { addTaskToQueue, removeTaskFromQueue } from '../../queue/offline';
import { inAppModalGroupRef } from '../../modals';
import { searchComponents } from '../../components';

export const useCustomerProductListForCart = () => {
  const { customerId } = useParams();
  const { showOutOfStockTag } = useAppSelector(getCatalogConfig);
  const appState = useAppSelector(getAppType);
  const previousCustomer = useAppSelector(getCustomerInfo);
  const currentCustomer = useAppSelector(getCurrentCustomer);
  const orderInfo = useAppSelector(getExtraDetails);
  const searchKey = useAppSelector(getSearchKey);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const checkIn = useAppSelector(getCheckInInfo);
  const clockIn = useAppSelector(getClockInInfo);
  const { disableRetailingWithoutCheckIn } =
    useAppSelector(getFieldAutomationConfig) || {};
  const app = useAppSelector(getAppType);
  const [getProductsListApi, getProductsListApiResponse] =
    useGetProductsByCustomerMutation();
  const customerCart = useAppSelector((state) =>
    getCartByCustomer(state, customerId!)
  );

  const [categoryTree, setCategoryTree] = useState<
    CategoryTreeItem | undefined
  >(undefined);
  const [segmentFilters, setSegmentFilters] = useState<ChipFilterSection[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<
    CategoryTreeItem[]
  >([]);
  const [updatedProducts, setUpdatedProducts] = useState<ProductPriceUpdateMap>(
    {}
  );
  const [showFilter, setShowFilter] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [successState, setSuccessState] = useState(false);
  const [selectedSegment, setSelectedSegment] = useState('');
  const { debouncedSearchValue } = useDebounceSearch();
  const [uomModalState, setUomModalState] = useState<{
    open: boolean;
    line?: ActiveOrderLine;
  }>({
    open: false,
    line: undefined,
  });

  const lastActionRef = useRef<
    'addProduct' | 'updateOrRemove' | 'replaceProduct' | 'addToCart' | null
  >(null);
  const totalRef = useRef<number | null>(null);

  const tripType: TripInfoTaskType | undefined = useMemo(() => {
    if (app !== APP_TYPE.FIELDS_OPS) return undefined;
    if (!clockIn || !clockIn.tripId) return TripInfoTaskType.NOT_CLOCKED_IN;
    if (disableRetailingWithoutCheckIn) {
      if (!checkIn) return TripInfoTaskType.NOT_CHECKED_IN;
      if (checkIn && checkIn.customerId !== customerId)
        return TripInfoTaskType.NOT_CHECKED_OUT;
    }
    return undefined;
  }, [clockIn, checkIn, disableRetailingWithoutCheckIn]);

  const triggerTripModal = (): boolean => {
    if (tripType) {
      inAppModalGroupRef?.current?.openTripInfoModal(tripType);
      return true;
    }
    return false;
  };

  const onSearchCancel = () => {
    dispatch(updateSearchKey(''));
  };

  const searchConfig = useMemo(
    () => ({
      enabled: showSearch,
      placeholderText: 'Search for Products',
      onSearchAction: () => {
        setShowSearch(false);
        dispatch(removeSearchKey());
      },
    }),
    [showSearch]
  );

  useEffect(() => {
    return () => {
      dispatch(updateProductLineItems([]));
    };
  }, []);

  const showProductAddedToCardToast = () => {
    if (lastActionRef.current === 'addToCart') {
      dispatch(
        showToast({
          message: 'Product added to cart successfully',
          type: ErrorVariants.SUCCESS,
          canDisplay: true,
          responseStatus: 'Success',
        })
      );
    }
    if (lastActionRef.current === 'updateOrRemove') {
      dispatch(
        showToast({
          message: 'Product updated successfully',
          type: ErrorVariants.SUCCESS,
          canDisplay: true,
          responseStatus: 'Success',
        })
      );
    }
    lastActionRef.current = null;
  };

  useEffect(() => {
    if (!isEmpty(categoryTree) || !isEmpty(selectedCategories)) return;
    const rootCategory = getProductsListApiResponse?.data?.categoryTree?.find(
      (category) => category?.categoryCode === '__root_category__'
    );

    if (!rootCategory || !isEmpty(selectedCategories)) return;

    const hasChildren = rootCategory && rootCategory.children?.length;
    const hasSingleChildWithChildren =
      rootCategory &&
      rootCategory.children?.length === 1 &&
      rootCategory.children[0]?.children?.length &&
      rootCategory.children[0]?.children?.length > 0;
    const hasMultipleChildren =
      rootCategory.children?.length && rootCategory?.children?.length > 1;

    if (hasChildren || hasSingleChildWithChildren || hasMultipleChildren) {
      setCategoryTree(rootCategory);
    }
  }, [
    getProductsListApiResponse?.data?.categoryTree,
    selectedCategories,
    categoryTree,
  ]);

  useEffect(() => {
    const segments = getProductsListApiResponse?.data?.segmentsSummary || [];
    if (segments.length === 0) return;

    const filters: ChipDataItem[] = segments.map(
      ({ segmentId, segmentName }) => ({
        id: segmentId,
        selected: selectedSegment === segmentId,
        value: segmentName,
      })
    );
    setSegmentFilters([
      {
        items: [
          { id: '', selected: selectedSegment === '', value: 'All' },
          ...filters,
        ],
        onItemPress: ({ id }) => setSelectedSegment(id),
      },
    ]);
  }, [getProductsListApiResponse?.data?.segmentsSummary, selectedSegment]);

  const fetchProducts = useCallback(
    (pageParams: { pageNo: number; maxPageSize: number }) => {
      getProductsListApi({
        workspaceId: sellerWorkspaceId,
        params: {
          customerId: customerId,
          pageNo: pageParams.pageNo,
          pageSize: pageParams.maxPageSize,
        },
        body: {
          sortBy: 'sortOrder',
          sortDirection: SortDirection.ASCENDING,
          customerId: customerId,
          includeCategoryTreeList: true,
        },
      });
    },
    [selectedCategories, selectedSegment]
  );

  const categoryCodes = useMemo(
    () => selectedCategories?.map((i) => i?.categoryCode).filter(Boolean),
    [selectedCategories]
  );

  const filterParams: FilterConfig<ProductVariant>[] = useMemo(
    () => [
      {
        type: 'array-object',
        field: 'segments',
        matchOn: 'segmentId',
        values:
          isNil(selectedSegment) || isEmpty(selectedSegment)
            ? []
            : [selectedSegment],
      },
      {
        type: 'array-object',
        field: 'categories',
        matchOn: 'categoryCode',
        values: categoryCodes,
      },
    ],
    [selectedSegment, categoryCodes]
  );

  const {
    data: productsData,
    isPageLoading,
    refetch: refetchProducts,
  } = useLoadAllApiDataV2<ProductVariant, ProductListResponse>({
    fetchDataApiCall: fetchProducts,
    apiResponse: getProductsListApiResponse,
    transformer: {
      getData: (res) =>
        res?.products?.flatMap(({ productVariants }) => productVariants) ?? [],
      getPagination: (res) => ({
        hasNextPage: (res?.endRecord ?? 0) < (res?.total ?? 0),
      }),
    },
    uniqueKey: 'id',
    maxPageSize: 200,
    dependencies: [selectedCategories, selectedSegment],
    searchText: debouncedSearchValue,
    searchPaths: ['name', 'sku'],
    filters: filterParams,
  });

  const createOrderLine = (
    product: ProductVariant,
    qty: number
  ): ActiveOrderLine => {
    const line: ActiveOrderLine = {
      sku: product?.sku,
      skuName: product?.name,
      divisionId: product?.division?.[0]?.divisionId,
      description: product?.shortDescription,
      unitPrice: product?.price?.toString(),
      productVariantId: Number(product?.productVariantId),
      quantity: qty,
      linePrice: product?.price?.toString(),
      minOrderQty: product?.minOrderQty || 1,
      promotionId: product?.promotions?.[0]?.id,
      promotionName: product?.promotions?.[0]?.promotion,
      productVariant: {
        additionalUOM: product?.additionalUOM,
        baseUom: product?.baseUom,
        packSize: product?.packSize,
      },
      categories: product?.categories,
      segments: product?.segments,
      facets: product?.facets,
    };
    // Preserve stock on cart line so cart screen can show OOS state.
    (line as ActiveOrderLine & { stock?: number }).stock = product?.stock;
    return line;
  };

  const addToOffline = async () => {
    await addTaskToQueue({
      type: OfflineTaskType.PLACE_ORDER,
      customer:
        currentCustomer ??
        (previousCustomer as OfflineCurrentCustomerMetadata | undefined),
      payload: {},
      onSuccess: showProductAddedToCardToast,
    });
  };

  useEffect(() => {
    if (!customerCart || customerCart?.lines.length === 0) {
      removeTaskFromQueue({
        type: OfflineTaskType.PLACE_ORDER,
        customerId: currentCustomer?.customerId!,
      });
    }
  }, [customerCart]);

  const onAddProductToCart = (product: ProductVariant) => {
    if (triggerTripModal()) return;
    lastActionRef.current = 'addToCart';
    const orderLineForCart: ActiveOrderLine = createOrderLine(
      product,
      product.minOrderQty ?? 1
    );

    const itemToAddInCart = {
      customerId: customerId!,
      line: orderLineForCart,
      customerName: orderInfo.customerName || '',
      companyName: orderInfo.companyName || '',
    };
    dispatch(addItemToCart(itemToAddInCart));
    addToOffline();
  };

  const onProductRemoveAndUpdate = (
    product: ProductVariant,
    type: OrderProductActionType
  ) => {
    if (triggerTripModal()) return;
    const line = createOrderLine(product, product?.newQty ?? product?.quantity);
    lastActionRef.current = 'updateOrRemove';
    if (type === OrderProductActionType.REMOVE) {
      dispatch(
        removeItemFromCart({
          customerId: customerId!,
          line: line,
        })
      );
    } else if (type === OrderProductActionType.EDIT) {
      dispatch(updateItemInCart({ customerId: customerId!, line: line }));
    }
    addToOffline();
  };

  const onReplaceProductPress = (product: ProductVariant) => {};

  const onUpdatePrice = (item: PriceUpdatedProduct) => {
    const { price, pvId, ptr } = item;
    const id = String(pvId);
    if (!updatedProducts[id]) {
      setUpdatedProducts((prev) => ({ ...prev, [id]: item }));
    } else {
      setUpdatedProducts((prev) => ({
        ...prev,
        [id]: { ...prev[id], price: Number(price), ptr: Number(ptr) },
      }));
    }
  };

  const onSaveCustomerPrices = (customerId: string) => {};

  const onOpenUomModal = (cartLine: any) => {
    setUomModalState({ open: true, line: cartLine });
  };

  const onCloseUomModal = () => {
    setUomModalState({ open: false, line: undefined });
  };

  const onUomChange = (uom: UOM) => {
    const lineItem = uomModalState.line;
    if (lineItem) {
      dispatch(
        updateItemInCart({
          customerId: customerId!,
          line: {
            ...lineItem,
            quantity: Number(lineItem.quantity) * uom.packSize,
          },
        })
      );
      onCloseUomModal();
    }
  };

  const searchProp =
    appState === APP_TYPE.FIELDS_OPS
      ? { search: searchComponents({ searchConfig, setShowSearch }) }
      : undefined;

  return {
    productsData,
    total: totalRef.current ?? 0,
    isPageLoading,
    onReplaceProductPress,
    onProductRemoveAndUpdate,
    categoryTree,
    setSelectedCategories,
    onSearchCancel,
    searchKey,
    isLoading:
      (getProductsListApiResponse.isUninitialized ||
        getProductsListApiResponse.isLoading ||
        isPageLoading) &&
      productsData?.length === 0,
    setShowFilter,
    showFilter,
    setShowSearch,
    showSearch,
    onUpdatePrice,
    updatedProducts,
    onSaveCustomerPrices,
    successState,
    setSuccessState,
    segmentFilters,
    setSelectedSegment,
    onAddProductToCart,
    searchConfig,
    onOpenUomModal,
    onCloseUomModal,
    uomModalState,
    onUomChange,
    searchProp,
    appState,
    showOutOfStockTag,
    refetchProducts,
  };
};
